import { Injectable } from '@angular/core';
import { jsPDF } from "jspdf";

@Injectable()
export abstract class PdfExportCertificatesVentusBaseService {
  protected doc: jsPDF;

  // A4 Size - 210 x 297 mm / 8.27 x 11.60 inches / 595 x 842 pt
  protected a4Width = 595;
  protected a4Height = 842;
  protected currentYValue = 0;

  protected readonly logoUrl = './assets/img/ventus-certificates/ventus_logo.png';
  protected readonly signatureUrl = './assets/img/ventus-certificates/ventus_cert_signature.png';
  protected readonly watermarkUrl = './assets/img/ventus-certificates/ventus_watermark.png';

  protected readonly fontSizePage1Header = 20;
  protected readonly fontSizePage1Body = 9;

  protected readonly fontSizePage2Header = 15;
  protected readonly fontSizePage2SubHeader = 7;
  protected readonly fontSizePage2Body = 6;

  protected readonly page2LeftMargin = 33;
  protected readonly page2LeftIndent = 66;
  protected readonly page2LineBreak = 15;
  protected readonly page2NewLine = 9;

  protected abstract fillInApprovedCodes(): void;

  public getCertificateFile(certificateDetails, currentDate, expiryDate): Blob {
    return this.createCertificate(certificateDetails.workerName, certificateDetails.company, currentDate, expiryDate);
  }

  public downloadCertificate(certificateDetails, currentDate, expiryDate): any {
    this.createCertificate(certificateDetails.workerName, certificateDetails.company, currentDate, expiryDate);

    const fileName = `${certificateDetails.siteName} - ${certificateDetails.inductionName} Certificate.pdf`;
    this.doc.save(fileName);
  }

  protected createCertificate(workerName, company, currentDate, expiryDate): Blob {
    this.doc = new jsPDF('p', 'pt', 'a4', true);

    this.createPage1StaticDetails();
    this.createPage1DynamicDetails(workerName, company, currentDate, expiryDate);

    this.doc.addPage();
    this.doc.setPage(2);
    this.createPage2();

    return this.doc.output('blob');
  }

  protected createPage1DynamicDetails(workerName: string, company: string, currentDate: string, expiryDate: string) {
    this.doc.setFont('helvetica', 'bold');

    // workerName and company
    this.splitTextOverManyLines(workerName, (this.a4Width / 2) - 80, 65, 150, 15);
    this.splitTextOverManyLines(company, (this.a4Width / 2) - 80, 350, 150, 15);

    // signature section
    this.doc.text('RAURI MAGUIRE', 175, 770);
    this.doc.text(currentDate, 480, 770);
    this.doc.text(expiryDate, 175, 790);
    this.doc.addImage(this.signatureUrl, 'PNG', 300, 763, 100, 0); // 0 for auto height

    // codes
    this.fillInApprovedCodes();

    this.doc.setFont('helvetica', 'normal');
  }

  protected createPage1StaticDetails() {
    this.setupPageBorderAndWaterMark();

    this.doc.addImage(this.logoUrl, 'PNG', 33, 65, 165, 0); // 0 for auto height

    this.doc.setFontSize(this.fontSizePage1Header);
    this.doc.text('CERTIFICATE OF AUTHORISATION', 218, 100);

    this.doc.setFontSize(this.fontSizePage1Body);

    this.doc.text('NAME:', 33, 150);
    this.doc.text('COMPANY:', 298, 150);

    let paragraph = "THE ABOVE-NAMED PERSON IS AUTHORISED IN THE FOLLOWING CATEGORIES FOR DUTIES UNDER THE VENTUS ENERGY ELECTRICAL AND MECHANICAL SAFETY RULES:";
    this.splitTextOverManyLines(paragraph, this.a4Width - 70, 33, 195, 15);

    paragraph = "AN AUTHORISATION COMMITTEE HAS EXAMINED THE ABOVE-NAMED CANDIDATE AND IS UNANIMOUSLY SATISFIED THAT THEY HAVE THE NECESSARY TECHNICAL KNOWLEDGE AND EXPERIENCE TO BE AUTHORISED UNDER THE VENTUS ENERGY ELECTRICAL AND MECHANICAL SAFETY RULES AND THE APPROVED PROCEDURES IN ACCORDANCE WITH THE AUTHORISATION CATEGORIES SPECIFIED ABOVE.";
    this.splitTextOverManyLines(paragraph, this.a4Width - 70, 33, 670, 15);

    this.doc.text('NAME', 175, 750);
    this.doc.text('SIGNATURE', 310, 750);
    this.doc.text('DATE', 480, 750);

    this.doc.text('AUTHORISING ENGINEER', 33, 770);
    this.doc.text('DATE OF EXPIRY', 33, 790);
  }

  protected createPage2() {
    this.setupPageBorderAndWaterMark();

    // main header
    this.doc.setFontSize(this.fontSizePage2Header);
    const header = 'AUTHORISATION CATEGORIES';
    let width = this.doc.getTextWidth(header);
    this.doc.text(header, (this.a4Width - width) / 2, 65);

    // Codes glossary
    this.currentYValue = 105;

    this.page2SubHeader('GROUP A - SENIOR AUTHORISED PERSON');
    this.page2CodeBlock(
      'A1_',
      [
        'Prepare, issue, transfer, suspend and cancel a **Permit To Work**.',
        'Decide what category of **Person** shall provide **Personal Supervision** when it is required.',
      ]
    );
    this.page2CodeBlock(
      'A2_',
      [
        'Prepare, issue, transfer and cancel a **Sanction For Test**.',
      ]
    );
    this.page2CodeBlock(
      'A3_',
      [
        'Prepare, issue, transfer, suspend and cancel a **Limitation Of Access** when work or testing is to be done outside **Safety Distances** but in close proximity to the limits of **Safety Distances**',
        'to **HV Equipment** and to decide whether to issue a **Limitation Of Access** in such circumstances.',
      ]
    );
    this.page2CodeBlock(
      'A4_',
      [
        'Prepare, issue, transfer suspend and cancel a **Limitation Of Access** when **Safety Distances** to **HV Equipment** do not have to be considered. To achieve  **Safety from the System** by',
        'limiting the work or testing or the work area of to decide whether a  **Limitation Of Access** is required to confirm oral instructions in such circumstances.',
      ]
    );
    this.page2CodeBlock(
      'A5_',
      [
        'Prepare, issue, transfer, suspend and cancel a **Permit To Work** for work or testing on **LV Equipment**.',
        'Decide whether work or testing on **LV Equipment** is to be carried out with the **Equipment Isolated**, or with the **Equipment Live** and in accordance with the relevant Risk Assessment and',
        'Method Statement, and to decide whether such work or testing is to be carried out under **Approved** Local Procedures, Oral Instructions, a **Permit to Work** or **Personal Supervision**.',
        'Decide what category of Person shall provide **Personal Supervision** when it is required.',
      ]
    );
    this.page2CodeBlock(
      'A6X',
      [
        'Carry out duties allocated to **Senior Authorised Persons** as specified in **Approved Procedures**.',
      ]
    );

    this.page2SubHeader('GROUP B - AUTHORISED PERSON');
    this.page2CodeBlock(
      'B1_',
      [
        'Apply and remove safety precautions to the instructions of **Control Persons**.',
      ]
    );
    this.page2CodeBlock(
      'B2X',
      [
        'Carry out duties allocated to **Authorised Persons** as specified in **Approved Procedures**.',
      ]
    );

    this.page2SubHeader('GROUP C - CONTROL PERSON');
    this.page2CodeBlock(
      'C1_',
      [
        'Carry out duties of a **Control Person** at a System Control Centre on specified **Equipment**.',
      ]
    );
    this.page2CodeBlock(
      'C2_',
      [
        'Carry out duties of a **Control Person** on that part of the **HV System** transferred to them under the relevant Delegated Control **Approved** Procedure.',
      ]
    );
    this.page2CodeBlock(
      'C3A',
      [
        'Carry out duties of a **Control Person** on **LV Equipment**.',
      ]
    );

    this.page2SubHeader('GROUP D - COMPETENT PERSON');
    this.page2CodeBlock(
      'D1_',
      [
        'Fit and remove Portable **Primary Earths** under the **Personal Supervision** of a **Senior Authorised Person**.',
      ]
    );
    this.page2CodeBlock(
      'D2_',
      [
        'Receive and clear a **Limitation Of Access**.',
      ]
    );
    this.page2CodeBlock(
      'D3_',
      [
        'Receive and clear a **Permit To Work**.',
      ]
    );
    this.page2CodeBlock(
      'D4_',
      [
        'Work on **Live LV Equipment** in accordance with an **Approved** Risk Assessment and Method Statement.',
      ]
    );
    this.page2CodeBlock(
      'D5_',
      [
        'Receive and clear a **Sanction For Test**.',
      ]
    );
    this.page2CodeBlock(
      'D6_',
      [
        'Fit and remove **Drain Earths**.',
      ]
    );
    this.page2CodeBlock(
      'D7X',
      [
        'Carry out duties allocated to **Competent Persons** as specified in **Approved** Procedures.',
      ]
    );

    this.page2SubHeader('GROUP E - PERSON');
    this.page2CodeBlock(
      'E1_',
      [
        'Gain access to Substations containing **HV Equipment** to perform duties in a manner such that **Safety Distances** will not be infringed and that **System** derived hazards will not cause danger.',
      ]
    );
    this.page2CodeBlock(
      'E2_',
      [
        'Work as a member of a **Working Party** on the **System** in accordance with the Ventus Energy Electrical & Mechanical Safety Rules and  **Approved** Procedures.',
      ]
    );

    this.page2SubHeader('GROUP F - SELECTED PERSON');
    this.page2CodeBlock(
      'F1_',
      [
        'Make reports and recommendations to overcome hazards which may prevent work or testing being performed safely on  **Equipment** which has otherwise been made safe.',
      ]
    );

    this.page2SubHeader('GROUP G - PERSON IN TRAINING');
    this.page2CodeBlock(
      'G1_',
      [
        'Carry out duties as directed by, and under the  **Personal Supervision** of a **Person** authorised to carry out such duties.',
      ]
    );

    this.page2SubHeader('GROUP H - AUTHORISING ENGINEER');
    this.page2CodeBlock(
      'H1',
      [
        'Carry out duties of an **Authorising Engineer** under the Ventus Energy Electrical and Mechanical Safety Rules.',
      ]
    );

    // extra line break
    this.currentYValue += this.page2LineBreak;

    this.doc.text('The 3rd character of the Authorisation Category is taken from the following list of subscripts \'A\' to \'G\'. Where the 3rd character is \'X\', this indicates that specific duties are detailed in this', this.page2LeftMargin, this.currentYValue);
    this.currentYValue += this.page2NewLine;
    this.doc.text('Certificate Of Authorisation', this.page2LeftMargin, this.currentYValue);

    this.currentYValue += this.page2LineBreak;
    this.page2CodeBlock('A', ['LV Equipment']);
    this.page2CodeBlock('B', ['Equipment nominally operating up to 33Kv']);
    this.page2CodeBlock('C', ['Equipment nominally operating up to 66kV']);
    this.page2CodeBlock('D', ['Equipment nominally operating up to 132Kv']);
    this.page2CodeBlock('E', ['Equipment nominally operating up to 275Kv']);
    this.page2CodeBlock('F', ['Equipment nominally operating up to 400kV']);
    this.page2CodeBlock('X', ['Specified in text.']);
  }

  protected page2SubHeader(subHeader: string) {
    this.doc.setFontSize(this.fontSizePage2SubHeader);
    this.doc.text(subHeader, this.page2LeftMargin, this.currentYValue);
    this.doc.setFontSize(this.fontSizePage2Body);

    this.currentYValue += this.page2LineBreak;
  }

  protected page2CodeBlock(code: string, lines: string[]) {
    // left column: Code
    this.doc.text(code, this.page2LeftMargin, this.currentYValue);

    // right column: explanation
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      this.textWithBoldInline(line, this.currentYValue);

      if (i !== lines.length - 1) {
        this.currentYValue += this.page2NewLine;
      }
    }

    this.currentYValue += this.page2LineBreak;
  }

  protected setupPageBorderAndWaterMark() {
    this.doc.setDrawColor(140, 140, 140);
    this.doc.rect(25, 25, this.a4Width - 50, this.a4Height - 50);

    this.doc.addImage(this.watermarkUrl, 'PNG', 56, 168, 483, 0); // 0 for auto height
  }

  protected textWithBoldInline(inputValue: string, yCoordinate) {
    const arrayOfNormalAndBoldText = inputValue.split('**');
    let xCoordinate = this.page2LeftIndent;

    arrayOfNormalAndBoldText.map((text, i) => {
      this.doc.setFont('helvetica', 'bold');
      // every even item is a normal font weight item
      if (i % 2 === 0) {
        this.doc.setFont('helvetica', 'normal');
      }
      this.doc.text(text, xCoordinate, yCoordinate);
      xCoordinate = xCoordinate + this.doc.getStringUnitWidth(text) * 6;
    });
  }

  protected splitTextOverManyLines(paragraph: string, textWidth: number, xCoordinate: number, startingYCoordinate: number, lineHeight: number) {
    const textArray = this.doc.splitTextToSize(paragraph, textWidth);
    let yCoordinate = startingYCoordinate;
    textArray.forEach(textPart => {
      this.doc.text(textPart.trim(), xCoordinate, yCoordinate);
      yCoordinate += lineHeight;
    });
  }
}
